import React, { useState } from 'react';
import { GeneratedResult } from "../lib/types";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { X, Info } from "lucide-react";

interface SettingsCustomizerProps {
  selectedResult: GeneratedResult | null;
  customSetting: string;
  onSettingChange: (setting: string) => void;
  onReset: () => void;
}

const getBackgroundSettings = (selectedResult: GeneratedResult) => [
  {
    id: 'room-1',
    name: 'Modern Living Room',
    imageUrl: `/${selectedResult.patternId}/${selectedResult.furnitureType}-${selectedResult.id.slice(-1)}/room-1.png`
  },
  {
    id: 'room-2',
    name: 'Contemporary Office',
    imageUrl: `/${selectedResult.patternId}/${selectedResult.furnitureType}-${selectedResult.id.slice(-1)}/room-2.png`
  },
  {
    id: 'room-3',
    name: 'Luxury Hotel Lobby',
    imageUrl: `/${selectedResult.patternId}/${selectedResult.furnitureType}-${selectedResult.id.slice(-1)}/room-3.png`
  },
  {
    id: 'room-4',
    name: 'Cozy Bedroom',
    imageUrl: `/${selectedResult.patternId}/${selectedResult.furnitureType}-${selectedResult.id.slice(-1)}/room-4.png`
  },
  {
    id: 'room-5',
    name: 'Executive Suite',
    imageUrl: `/${selectedResult.patternId}/${selectedResult.furnitureType}-${selectedResult.id.slice(-1)}/room-5.png`
  },
  {
    id: 'room-6',
    name: 'Boutique Restaurant',
    imageUrl: `/${selectedResult.patternId}/${selectedResult.furnitureType}-${selectedResult.id.slice(-1)}/room-6.png`
  },
  {
    id: 'room-7',
    name: 'Gallery Space',
    imageUrl: `/${selectedResult.patternId}/${selectedResult.furnitureType}-${selectedResult.id.slice(-1)}/room-7.png`
  },
  {
    id: 'room-8',
    name: 'Wellness Center',
    imageUrl: `/${selectedResult.patternId}/${selectedResult.furnitureType}-${selectedResult.id.slice(-1)}/room-8.png`
  }
];

export function SettingsCustomizer({ 
  selectedResult, 
  onReset 
}: SettingsCustomizerProps) {
  if (!selectedResult) {
    return null;
  }

  const backgroundSettings = getBackgroundSettings(selectedResult);

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        {/* Go Back Button - Mobile */}
        <div className="lg:hidden">
          <Button variant="outline" size="sm" onClick={onReset}>
            Go Back
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 lg:gap-6">
          {/* Selected Item - White Background */}
          <div className="lg:col-span-1">
            <Card>
              <CardContent className="p-3 lg:p-4">
                <div className="mb-3 hidden lg:block">
                  <Button variant="outline" size="sm" onClick={onReset} className="w-full">
                    Go Back
                  </Button>
                </div>
                <div className="aspect-square bg-white flex items-center justify-center rounded-lg border">
                  <img
                    src={selectedResult.imageUrl}
                    alt={`Selected ${selectedResult.furnitureType}`}
                    className="w-full h-full object-contain p-3 lg:p-4"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      target.parentElement!.innerHTML = `
                        <div class="w-full h-full flex items-center justify-center bg-white">
                          <div class="text-center">
                            <div class="w-12 h-12 lg:w-16 lg:h-16 bg-gray-200 rounded-lg mx-auto mb-2"></div>
                            <p class="text-xs text-gray-500">${selectedResult.furnitureType}</p>
                          </div>
                        </div>
                      `;
                    }}
                  />
                </div>
                <div className="mt-2 lg:mt-3 text-center">
                  <p className="text-xs lg:text-sm font-medium text-gray-900 capitalize">
                    {selectedResult.furnitureType}
                  </p>
                  <p className="text-xs text-gray-600">
                    White Background
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Background Settings */}
          <div className="lg:col-span-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 lg:gap-3">
              {backgroundSettings.map((setting) => (
                <Card 
                  key={setting.id} 
                  className="hover:shadow-md transition-shadow"
                >
                  <CardContent className="p-1 lg:p-2">
                    <div className="aspect-[4/3] bg-gray-100 rounded-lg overflow-hidden relative">
                      <img
                        src={setting.imageUrl}
                        alt={setting.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          target.parentElement!.innerHTML = `
                            <div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                              <div class="text-center">
                                <div class="w-8 h-8 lg:w-12 lg:h-12 bg-gray-300 rounded mx-auto mb-2"></div>
                              </div>
                            </div>
                          `;
                        }}
                      />
                    </div>

                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Banner */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-6">
        <div className="flex items-center gap-2 text-gray-700">
          <Info className="h-4 w-4" />
          <p className="text-sm font-medium">
            This is a limited preview. The platform will generate 100+ variations of each pattern. Additional actions such as upscale, edit prompt, etc. will be available.
          </p>
        </div>
      </div>



    </div>
  );
} 